import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { dropTask, task, timeout } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import ENV from "glesys-controlpanel/config/environment";
import { statusPageNotices } from "glesys-controlpanel/data/status-page/builders";
import dayjs from "dayjs";
import { allRouteParams } from "glesys-controlpanel/utils/url";
import { getOwnConfig, importSync, macroCondition } from "@embroider/macros";

export default class LoginController extends Controller {
  queryParams = ["status"];

  @service intl;
  @service session;
  @service router;
  @service store;

  username = null;
  password = null;
  otp = null;
  verificationCode = null;

  @tracked error = null;
  @tracked status = null;
  @tracked serviceInfo = [];
  @tracked displayOneTimePassword = false;
  @tracked displayVerificationCode = false;

  /* istanbul ignore next */
  get devPanel() {
    if (macroCondition(getOwnConfig().devtools)) {
      return importSync("./dev-panel").default;
    }

    return undefined;
  }

  get statusMessage() {
    if (this.displayVerificationCode) {
      return this.intl.t("login.status.verification-code");
    }

    switch (this.status) {
      case "logout":
        return this.intl.t("login.status.logged-out");
      case "logout.timeout":
        return this.intl.t("login.status.logged-out-time-out");
      case "timeout":
        return this.intl.t("login.status.timed-out");
      case "reset":
        return this.intl.t("login.status.reset");
      case "updated-to-external-2fa":
        return this.intl.t("login.status.2fa-updated");
      case "revert-to-email-based":
        return this.intl.t("login.status.revert-to-email-based-2fa");
      case "inactive":
        return this.intl.t("login.status.logged-out-inactive");
      default:
        return "";
    }
  }

  get successMessage() {
    switch (this.status) {
      case "confirmed":
        return this.intl.t("login.status.email-confirmed");
      default:
        return "";
    }
  }

  get errorMessage() {
    switch (this.status) {
      case "confirmation-failed":
        return this.intl.t("login.status.confirmation-failed");
      default:
        return this.error;
    }
  }

  get hasActiveMaintenance() {
    return this.serviceInfo.notices?.some(
      (notice) => notice.type === "planned" && dayjs().isAfter(dayjs(notice.begins_at).subtract(1, "hour")),
    );
  }

  get hasActiveIncident() {
    return this.serviceInfo.notices?.some((notice) => notice.type === "unplanned");
  }

  @action
  onFormChange({ username, password, otp, verificationCode }) {
    this.username = username ?? this.username;
    this.password = password ?? this.password;
    this.otp = otp ?? this.otp;
    this.verificationCode = verificationCode ?? this.verificationCode;
  }

  @action
  resetLogin() {
    this.resetForm();
  }

  setupController() {
    this.fetchStatusPage.perform();
  }

  resetController() {
    this.fetchStatusPage.cancelAll();
    this.scheduleStatusPageFetch.cancelAll();
    this.resetForm();
  }

  resetForm() {
    this.status = null;
    this.error = null;
    this.displayOneTimePassword = false;
    this.displayVerificationCode = false;
    this.username = null;
    this.password = null;
    this.otp = null;
    this.verificationCode = null;
  }

  loginTask = dropTask(async () => {
    if (isEmpty(this.username) || isEmpty(this.password)) {
      return;
    }

    this.status = null;
    this.error = null;

    try {
      await this.session.open.perform(this.username, this.password, this.otp, this.verificationCode);
      let transition = this.session.attemptedTransition;

      if (!transition) {
        return await this.router.transitionTo("dashboard").followRedirects();
      }

      let routeParams = allRouteParams(transition.to);

      await this.router
        .transitionTo(transition.to.name, ...routeParams, { queryParams: transition.to.queryParams })
        .followRedirects();
    } catch (error) {
      if (!error.content?.response?.status?.code) {
        throw error;
      }

      switch (error.content?.response?.status?.code) {
        case 429:
          this.error = this.intl.t("login.error.too-many-requests");
          break;
        case 1601:
          this.error = this.intl.t("login.error.bad-credentials");
          break;
        case 1603:
        case 1604:
          this.displayOneTimePassword = true;
          break;
        case 1606:
          this.error = this.intl.t("login.error.fraud-ip");
          break;
        case 1607:
          this.error = this.intl.t("login.error.disabled-user");
          break;
        case 1608:
          this.displayVerificationCode = true;
          break;
        case 1609:
          this.error = this.intl.t("login.error.bad-verification-code");
          break;
        default:
          this.error = error.content?.response?.status?.text;
      }
    }
  });

  fetchStatusPage = task(async () => {
    let { content } = await this.store.request(statusPageNotices());
    this.serviceInfo = content;

    if (ENV.environment !== "test") {
      this.scheduleStatusPageFetch.perform();
    }
  });

  scheduleStatusPageFetch = task(async () => {
    await timeout(1000 * 60);
    this.fetchStatusPage.perform();
  });
}
